'use client'

import React, { FC, ReactElement } from 'react'
import HeaderLink from '@/components/header-link'
import HeaderSocialLink from '@/components/header-social-link'
import Link from '@/components/link'
import HeaderProfile from '@/components/header-profile'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import formatNumber from '@/utils/format-number'
import FeeSelector from '@/components/fee-selector'
import FooterSubscribe from '@/components/footer-v2/subscribe'
import BitcoinIcon from '@/icons/bitcoin-filled'
import { Cog } from 'lucide-react'
import TwitterIcon from '@/icons/twitter'
import GitbookIcon from '@/icons/gitbook'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import event from '@/utils/event'

import Drawer from '@mui/material/Drawer'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

/// The fixed footer's contents, restated for the drawer: phones trim the
/// bar down to price, gear and the email sign-up, so everything it carries
/// on wider screens lives here too. Its own component so the price and fee
/// polls only run while the drawer is open.
const DrawerFooter: FC = (): ReactElement => {
	const { exchangeRate } = useExchangeRate()
	const { feeRate } = useFeeEstimates()

	return (
		<div data-testid="drawer-footer">
			<div className="flex items-center justify-between border-y border-border py-3 text-sm text-foreground">
				<p className="flex items-center gap-2">
					<BitcoinIcon className="size-4" />
					{exchangeRate ? `$${formatNumber(exchangeRate.toFixed(2))}` : '—'}
				</p>
				{/* The drawer sits above the default popover layer. */}
				<FeeSelector align="end" contentClassName="z-[1400]">
					<div
						className="flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground"
						data-testid="drawer-fee-cluster"
					>
						{feeRate ? `${Number(feeRate.toFixed(2))} sats/vB` : '—'}
						<Cog className="size-4" />
					</div>
				</FeeSelector>
			</div>
			<FooterSubscribe stacked className="border-b border-border py-4" />
			<a
				href="mailto:support@ordinalswallet.com"
				className="block pt-4 text-xs text-muted-foreground hover:text-foreground"
			>
				support@ordinalswallet.com
			</a>
		</div>
	)
}

const HeaderDrawer: FC<{ links: any }> = (props): ReactElement => {
	const loggedIn = useLoggedIn()
	const [open, setOpen] = React.useState(false)

	const toggle = React.useCallback(() => {
		setOpen((value) => !value)
	}, [])

	const handleConnectWallet = React.useCallback((evt: any) => {
		evt.stopPropagation()
		evt.preventDefault()
		event.emit('command', { id: 'connect-wallet' })
		setOpen(false)
	}, [])

	const renderLink = React.useCallback(
		(e: any, i: any) => (
			<HeaderLink
				className="py-4 -my-4"
				name={e.name}
				href={e.href}
				key={i}
				dropdown={e.dropdown}
				onClick={toggle}
			/>
		),
		[toggle]
	)

	const _handleSubmit = React.useCallback(() => {
		setOpen(false)
	}, [])

	return (
		<>
			<div onClick={toggle} role="button" tabIndex={0} onKeyDown={onKeyboardClick(toggle)}>
				<IconButton aria-label="Open menu" className="size-5">
					<MenuIcon className="fill-foreground" />
				</IconButton>
			</div>
			<Drawer
				anchor={'right'}
				open={open}
				onClose={toggle}
				classes={{
					paper: '!bg-background !text-foreground'
				}}
				slotProps={{
					backdrop: {
						className: 'backdrop-blur-md bg-foreground/30'
					}
				}}
			>
				<div className="p-6 w-[300px] grid justify-center grid-cols-1 gap-6">
					<Link href="/" onClick={toggle}>
						<img className="h-8" src="/logo-icon.svg" alt="Ordinals Wallet" />
					</Link>
					{props?.links?.map(renderLink)}
					<HeaderLink className="py-4 -my-4" name="Activity" href="/activity" onClick={toggle} />
					<HeaderLink className="py-4 -my-4" name="Mempool" href="/mempool" onClick={toggle} />
					<HeaderLink className="py-4 -my-4" name="Donate" href="/donate" onClick={toggle} />
					<HeaderLink className="py-4 -my-4" name="Buy/Sell BTC" href="/markets/bitcoin" onClick={toggle} />
					<HeaderLink
						className="py-4 -my-4"
						name="Creator Dashboard"
						href="/creator-dashboard"
						onClick={toggle}
					/>
					{loggedIn ? (
						<>
							{[{ name: 'My wallet', href: '/wallet' }].map(renderLink)}
							<HeaderProfile onClick={toggle} showCharacterCount={4} />
						</>
					) : (
						<>
							<ButtonGradientEpic
								className="rounded-full h-11 text-sm"
								onClick={handleConnectWallet}
							>
								Connect Wallet
							</ButtonGradientEpic>
						</>
					)}
					<div className="flex items-center justify-between gap-4">
						<div className="flex gap-4">
							<HeaderSocialLink icon={TwitterIcon} href="https://twitter.com/ordinalswallet" />
							<HeaderSocialLink icon={GitbookIcon} href="https://blog.ordinalswallet.com/docs" />
						</div>
						<ThemeToggle />
					</div>
					<DrawerFooter />
				</div>
			</Drawer>
		</>
	)
}

export default HeaderDrawer
